import escapeHtml from "escape-html";
import TileManager from "../letters/tileManager";
import type LettersTile from "../letters/lettersTile";
import { calculateScore, checkConnectivity, type ScoreResult } from "./scoring";
import {
  generateLetters,
  generateBoard,
  decodeBoard,
  buildSerializedState,
} from "./utils";
import type {
  WordSpiderBoard,
  WordSpiderSeedPayload,
  WordSpiderSerializedState,
  WordSpiderBonusType,
  WordSpiderCell,
} from "./types";
import {
  fetchLeaderboard,
  fetchPersonalBest,
  type WordSpiderLeaderboardEntry,
} from "./leaderboard";

class WordSpiderGame {
  private readonly gameContainer: HTMLElement;
  private readonly boardContainer: HTMLElement;
  private readonly lettersContainer: HTMLElement;
  private readonly newGameButton: HTMLElement | null;
  private readonly scoreButton: HTMLElement | null;
  private readonly resetButton: HTMLElement | null;
  private readonly leaderboardButton: HTMLElement | null;
  private readonly tileManager: TileManager;
  private readonly gridSize: number;
  private readonly letterCount: number;
  private readonly initialSeedData: WordSpiderSeedPayload | null;
  private seedData: WordSpiderSeedPayload | null;
  private bonusBoard: WordSpiderBoard;
  private letterRack: string[];
  private leaderboardPanel: HTMLElement | null;
  private leaderboardLoading: boolean;
  private selectedSquareId: string | null;
  private typingDirection: "horizontal" | "vertical";
  private readonly liveScore = document.querySelector<HTMLOutputElement>("[data-wordspider-live-score]");
  private readonly scoreLabel = document.querySelector<HTMLElement>("[data-wordspider-status-label]");
  private readonly bestScore = document.querySelector<HTMLOutputElement>("[data-wordspider-best]");
  private readonly bestNote = document.querySelector<HTMLElement>("[data-wordspider-best-note]");
  private personalBestScore: number | null = null;
  private scoreSaveTimer: number | null = null;

  constructor() {
    this.gridSize = 7;
    this.letterCount = 20;

    // Get containers
    this.gameContainer = document.querySelector("[data-wordspider-game]");
    this.boardContainer = document.querySelector("[data-wordspider-board]");
    this.lettersContainer = document.querySelector("[data-wordspider-letters]");
    this.newGameButton = document.querySelector("[data-wordspider-new-game]");
    this.scoreButton = document.querySelector("[data-wordspider-score]");
    this.resetButton = document.querySelector("[data-wordspider-reset]");
    this.leaderboardButton = document.querySelector(
      "[data-wordspider-leaderboard]",
    );

    if (!this.gameContainer || !this.boardContainer || !this.lettersContainer) {
      throw new Error("WordSpider: Required containers not found");
    }

    this.boardContainer.addEventListener("wordspider:tile-drop", (event) => {
      const target = event.target as HTMLElement | null;
      if (!target?.id || !target.hasAttribute("data-board-square")) {
        return;
      }
      this.animateBonusPlacement(target);

      const detail = event instanceof CustomEvent ? event.detail : null;
      const hasCoords =
        detail &&
        typeof detail.row === "number" &&
        typeof detail.col === "number";

      if (hasCoords) {
        const baseRow = detail.row as number;
        const baseCol = detail.col as number;
        const nextRow =
          this.typingDirection === "vertical" ? baseRow + 1 : baseRow;
        const nextCol =
          this.typingDirection === "horizontal" ? baseCol + 1 : baseCol;

        if (
          nextRow >= 0 &&
          nextRow < this.gridSize &&
          nextCol >= 0 &&
          nextCol < this.gridSize
        ) {
          this.selectSquare(nextRow, nextCol);
          return;
        }
      }

      this.setSelectedSquare(target.id);
    });

    document.addEventListener(
      "pointerdown",
      (event) => {
        const target = event.target as HTMLElement | null;
        if (!target) {
          return;
        }

        const boardSquare = target.closest<HTMLElement>("[data-board-square]");
        if (boardSquare?.id) {
          this.setSelectedSquare(boardSquare.id);
          return;
        }

        const letterTile = target.closest<HTMLElement>("[data-letter-id]");
        if (letterTile?.id) {
          const tileId =
            letterTile.getAttribute("data-letter-id") ?? letterTile.id;
          const selectorId = tileId ? this.escapeCssSelector(tileId) : tileId;
          const occupyingSquare = selectorId
            ? this.boardContainer.querySelector<HTMLElement>(
                `[data-letter="${selectorId}"]`,
              )
            : null;
          if (occupyingSquare?.id) {
            this.setSelectedSquare(occupyingSquare.id);
            return;
          }
          return;
        }

        // Clicked outside the board or tiles on it, clear selection
        this.clearSelectedSquare();
      },
      { capture: true },
    );

    this.letterRack = [];
    this.leaderboardPanel = null;
    this.leaderboardLoading = false;
    this.selectedSquareId = null;
    this.typingDirection = "horizontal";
    this.updateTypingDirectionIndicator();

    this.initialSeedData = this.parseSeedData();
    this.seedData = this.initialSeedData;

    // Initialize single manager for the entire game container
    this.tileManager = new TileManager(this.gameContainer);

    // Generate bonus board layout
    this.bonusBoard = this.createBonusBoard();

    // Initialize the game
    this.initializeGame();
    this.attachEventListeners();
    // All placement paths (typing, clicks, dragging, removal, and reset) update
    // these board attributes. Observe the completed change, not intermediate moves.
    new MutationObserver(() => this.updateLiveScore()).observe(this.boardContainer, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ["data-letter"],
    });
    void this.loadPersonalBest();
  }

  private parseSeedData(): WordSpiderSeedPayload | null {
    const globalSeed = this.getSeedFromGlobal();
    if (globalSeed) {
      return globalSeed;
    }
    return null;
  }

  private getSeedFromGlobal(): WordSpiderSeedPayload | null {
    const payload = JSON.parse(document.getElementById("wordspider-seed")?.textContent || "null");
    if (!payload) {
      return null;
    }
    return this.validateSeedPayload(payload);
  }

  private validateSeedPayload(payload: unknown): WordSpiderSeedPayload | null {
    if (!payload || typeof payload !== "object") {
      return null;
    }

    const maybe = payload as Record<string, unknown>;
    const seed = typeof maybe.seed === "string" ? maybe.seed.trim() : "";
    const board = typeof maybe.board === "string" ? maybe.board.trim() : "";
    const letters =
      typeof maybe.letters === "string" ? maybe.letters.trim() : "";
    const source = typeof maybe.source === "string" ? maybe.source : undefined;

    if (!seed || board.length !== this.gridSize * this.gridSize * 2) {
      return null;
    }

    if (letters.length !== this.letterCount) {
      return null;
    }

    return {
      seed,
      board,
      letters,
      source,
    };
  }

  private initializeGame(): void {
    this.createBoard();
    this.createLetters();
    this.updateLiveScore();
  }

  private cancelPendingScoreSave(): void {
    if (this.scoreSaveTimer !== null) window.clearTimeout(this.scoreSaveTimer);
    this.scoreSaveTimer = null;
  }

  private updateLiveScore(): void {
    const board = this.convertBoardForScoring();
    const connected = checkConnectivity(board);
    const result = calculateScore(board);
    const errors: string[] = [];
    if (!connected) errors.push("Connect all placed letters.");
    if (result.invalidWords.length) {
      errors.push(`Invalid words: ${result.invalidWords.join(", ")}.`);
    }
    if (!errors.length && !result.validWords.length && board.some(row => row.some(cell => cell?.letter))) {
      errors.push("Connect at least two letters to make a word.");
    }
    if (this.scoreLabel) this.scoreLabel.textContent = errors.length ? "Board error" : "Board score";
    if (this.liveScore) {
      this.liveScore.textContent = errors.length ? errors.join(" ") : `${result.score} pts`;
      this.liveScore.classList.toggle("score-strip__value--error", errors.length > 0);
      this.liveScore.title = this.formatWordScoreSummary(result);
    }

    this.cancelPendingScoreSave();
    if (this.seedData && !errors.length && result.score > (this.personalBestScore ?? 0)) {
      // Display immediately; save only after a short pause in rapid typing.
      this.scoreSaveTimer = window.setTimeout(() => {
        this.scoreSaveTimer = null;
        if (result.score > (this.personalBestScore ?? 0)) void this.submitHighScore(board, result);
      }, 300);
    }
  }

  private renderPersonalBest(): void {
    if (this.bestScore) {
      this.bestScore.textContent = !this.seedData ? "—"
        : this.personalBestScore === null ? "…" : `${this.personalBestScore} pts`;
    }
    if (this.bestNote) this.bestNote.textContent = this.seedData ? "This puzzle" : "Unranked board";
  }

  private async loadPersonalBest(): Promise<void> {
    const seed = this.seedData?.seed;
    if (!seed) return;
    try {
      const score = await fetchPersonalBest(seed);
      if (this.seedData?.seed !== seed) return;
      // A save may finish before this initial read. Never roll a confirmed best back.
      this.personalBestScore = Math.max(this.personalBestScore ?? 0, score);
      this.renderPersonalBest();
    } catch (error) {
      if (this.seedData?.seed !== seed || this.personalBestScore !== null) return;
      if (this.bestScore) this.bestScore.textContent = "—";
      if (this.bestNote) this.bestNote.textContent = "Best unavailable";
      console.warn("WordSpider: Failed to load personal best", error);
    }
  }

  private createBoard(): void {
    // Create 7x7 grid of empty tiles with bonuses
    for (let row = 0; row < this.gridSize; row++) {
      for (let col = 0; col < this.gridSize; col++) {
        const id = `board-${row}-${col}`;
        const tile = this.tileManager.createTile({
          id,
          letter: "",
          isEmpty: true,
        });

        const tileElement = tile.getElement();
        tileElement.setAttribute("data-board-square", "true");
        tileElement.dataset.row = String(row);
        tileElement.dataset.col = String(col);
        tileElement.disabled = false;
        tileElement.removeAttribute("aria-disabled");
        tileElement.setAttribute("tabindex", "-1");
        tileElement.addEventListener("pointerdown", (event) => {
          if (event.button !== undefined && event.button !== 0) {
            return;
          }
          event.preventDefault();
          this.selectSquare(row, col);
        });
        tileElement.addEventListener("click", (event) => {
          event.preventDefault();
          this.selectSquare(row, col);
        });
        const bonus = this.bonusBoard.cells[row][col].bonus;

        // Add bonus information if present
        if (bonus !== "none") {
          tileElement.setAttribute("data-bonus", bonus);

          // Add bonus text overlay
          const bonusOverlay = document.createElement("div");
          bonusOverlay.className = "wordspider-bonus-overlay";
          bonusOverlay.textContent = this.getBonusDisplayText(bonus);
          tileElement.appendChild(bonusOverlay);

          // Add click handler for bonus squares

          // Add bonus styling class
          tileElement.classList.add(
            `wordspider-bonus--${bonus.toLowerCase().replace(/[^a-z0-9]/g, "-")}`,
          );
        }

        // Move tile to board container
        this.boardContainer.appendChild(tileElement);
      }
    }
  }

  private createBonusBoard(): WordSpiderBoard {
    const boardString = this.seedData?.board;
    if (boardString) {
      try {
        return decodeBoard(boardString);
      } catch (error) {
        console.warn(
          `WordSpider: Failed to decode board "${boardString}", using random board`,
          error,
        );
      }
    }
    return generateBoard();
  }

  private createLetters(): void {
    // Generate 20 random letters with proper frequency distribution
    const letters = this.getLettersForGame();
    this.letterRack = [...letters];

    for (let i = 0; i < letters.length; i++) {
      const id = `letter-${i}`;
      const tile = this.tileManager.createTile({
        id,
        letter: letters[i],
        isEmpty: false,
      });
      // Move tile to letters container
      this.lettersContainer.appendChild(tile.getElement());
    }
  }

  private attachEventListeners(): void {
    if (this.newGameButton) {
      this.newGameButton.addEventListener("click", () => {
        this.newGame();
      });
    }

    if (this.scoreButton) {
      this.scoreButton.addEventListener("click", () => {
        this.calculateAndShowScore();
      });
    }

    if (this.resetButton) {
      this.resetButton.addEventListener("click", () => {
        this.resetBoard();
      });
    }

    if (this.leaderboardButton) {
      this.leaderboardButton.addEventListener("click", () => {
        void this.toggleLeaderboard();
      });
    }

    document.addEventListener("keydown", (event) => {
      if (event.metaKey || event.ctrlKey) {
        return;
      }
      if (this.shouldIgnoreKeyEvent(event)) {
        return;
      }

      if (this.handleKeydown(event)) {
        event.preventDefault();
      }
    });

    window.addEventListener(
      "resize",
      () => {
        this.realignPlacedTiles();
      },
      { passive: true },
    );

    this.lettersContainer.addEventListener("click", (event) => {
      const target = event.target as HTMLElement | null;
      const tileElement = target?.closest<HTMLElement>("[data-letter-id]");
      if (!tileElement) {
        return;
      }

      if (tileElement.classList.contains("letters-tile--dragging")) {
        return;
      }

      this.handleAvailableTileClick(tileElement);
    });

    // Placed tiles remain in the rack's DOM and are translated onto the board.
    const returnPlacedTile = (event: Event) => {
      const target = event.target as HTMLElement | null;
      const tileElement = target?.closest<HTMLElement>("[data-letter-id]");
      if (!tileElement) return;
      const tileId =
        tileElement.getAttribute("data-letter-id") ?? tileElement.id;
      const square = this.boardContainer.querySelector<HTMLElement>(
        `[data-letter="${this.escapeCssSelector(tileId)}"]`,
      );
      if (!square) return;

      event.preventDefault();
      this.clearSquareOccupant(square);
      this.setSelectedSquare(square.id);
    };
    this.lettersContainer.addEventListener("dblclick", returnPlacedTile);
    this.lettersContainer.addEventListener(
      "wordspider:tile-double-tap",
      returnPlacedTile,
    );
  }

  private shouldIgnoreKeyEvent(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;
    if (!target) {
      return false;
    }

    if (target.closest("input, textarea, select, [contenteditable]")) {
      return true;
    }

    if (
      target.tagName === "BUTTON" &&
      !target.hasAttribute("data-board-square")
    ) {
      return true;
    }

    return false;
  }

  private handleKeydown(event: KeyboardEvent): boolean {
    const key = event.key;

    if (key === "Backspace") {
      return this.handleBackspace();
    }

    if (key === " " || key === "Spacebar") {
      this.toggleTypingDirection();
      return true;
    }

    switch (key) {
      case "ArrowLeft":
        this.moveSelection(0, -1);
        return true;
      case "ArrowRight":
        this.moveSelection(0, 1);
        return true;
      case "ArrowUp":
        this.moveSelection(-1, 0);
        return true;
      case "ArrowDown":
        this.moveSelection(1, 0);
        return true;
      default:
        break;
    }

    if (key.length === 1 && /^[a-z]$/i.test(key)) {
      const upper = key.toUpperCase();
      return this.handleLetterInput(upper);
    }

    return false;
  }

  private handleLetterInput(letter: string): boolean {
    if (!this.selectedSquareId) {
      return false;
    }

    const squareElement = document.getElementById(this.selectedSquareId);
    if (!squareElement) {
      this.clearSelectedSquare();
      return false;
    }

    const tile = this.findAvailableLetterTile(letter);
    if (!tile) {
      return false;
    }

    this.placeTileOnSquare(tile, squareElement);
    this.advanceSelectionAfterPlacement();
    return true;
  }

  private handleBackspace(): boolean {
    if (!this.selectedSquareId) {
      return false;
    }

    const squareElement = document.getElementById(this.selectedSquareId);
    if (!squareElement) {
      this.clearSelectedSquare();
      return false;
    }

    this.clearSquareOccupant(squareElement);

    const deltaRow = this.typingDirection === "horizontal" ? 0 : -1;
    const deltaCol = this.typingDirection === "horizontal" ? -1 : 0;
    this.moveSelection(deltaRow, deltaCol);
    return true;
  }

  private handleAvailableTileClick(tileElement: HTMLElement): void {
    const tileId = tileElement.getAttribute("data-letter-id") ?? tileElement.id;
    const selectorId = this.escapeCssSelector(tileId);
    const occupyingSquare = this.boardContainer.querySelector<HTMLElement>(
      `[data-letter="${selectorId}"]`,
    );
    if (occupyingSquare) {
      // A single click selects a placed tile without moving it between clicks.
      this.setSelectedSquare(occupyingSquare.id);
      return;
    }
    if (!this.selectedSquareId) return;

    const squareElement = document.getElementById(this.selectedSquareId);
    if (!squareElement) {
      this.clearSelectedSquare();
      return;
    }

    const tile = tileId ? this.tileManager.getTileById(tileId) : null;
    if (!tile) {
      return;
    }

    this.placeTileOnSquare(tile, squareElement);
    this.advanceSelectionAfterPlacement();
  }

  private realignPlacedTiles(): void {
    const boardSquares = Array.from(
      this.boardContainer.querySelectorAll<HTMLElement>(
        "[data-board-square][data-letter]",
      ),
    );
    for (const square of boardSquares) {
      const tileId = square.getAttribute("data-letter");
      if (!tileId) {
        continue;
      }
      const tile = this.tileManager.getTileById(tileId);
      if (!tile) {
        continue;
      }
      tile.resetTransform();
      const tileRect = tile.getBoundingClientRect();
      const squareRect = square.getBoundingClientRect();
      const dx =
        squareRect.left +
        squareRect.width / 2 -
        (tileRect.left + tileRect.width / 2);
      const dy =
        squareRect.top +
        squareRect.height / 2 -
        (tileRect.top + tileRect.height / 2);
      tile.setTransform(`translate3d(${dx}px, ${dy}px, 0)`);
    }
  }

  private findAvailableLetterTile(letter: string): LettersTile | null {
    const selector = `[data-letter-tile][data-letter="${letter}"]`;
    const candidates = Array.from(
      this.lettersContainer.querySelectorAll<HTMLElement>(selector),
    );

    for (const candidate of candidates) {
      if (!candidate.id) {
        continue;
      }
      if (this.isTileOnBoard(candidate.id)) {
        continue;
      }
      const tile = this.tileManager.getTileById(candidate.id);
      if (tile) {
        return tile;
      }
    }

    return null;
  }

  private isTileOnBoard(tileId: string): boolean {
    if (!tileId) {
      return false;
    }
    const selectorId = this.escapeCssSelector(tileId);
    return (
      this.boardContainer.querySelector<HTMLElement>(
        `[data-letter="${selectorId}"]`,
      ) !== null
    );
  }

  private placeTileOnSquare(
    tile: LettersTile,
    squareElement: HTMLElement,
  ): void {
    this.detachTileFromCurrentSquare(tile.getId());
    this.clearSquareOccupant(squareElement);

    tile.resetTransform();
    const tileRect = tile.getBoundingClientRect();
    const squareRect = squareElement.getBoundingClientRect();
    const tileCenterX = tileRect.left + tileRect.width / 2;
    const tileCenterY = tileRect.top + tileRect.height / 2;
    const squareCenterX = squareRect.left + squareRect.width / 2;
    const squareCenterY = squareRect.top + squareRect.height / 2;
    const dx = squareCenterX - tileCenterX;
    const dy = squareCenterY - tileCenterY;

    tile.setTransform(`translate3d(${dx}px, ${dy}px, 0)`);
    tile.setZIndex(1000);
    squareElement.setAttribute("data-letter", tile.getId());
    this.animateBonusPlacement(squareElement);
  }

  private animateBonusPlacement(square: HTMLElement): void {
    if (!square.dataset.bonus || window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    const color = getComputedStyle(square).backgroundColor;
    const face = document.getElementById(square.dataset.letter ?? "")?.querySelector(".letters-tile__face");
    // Animate the face, never the tile's transform, which controls its board position.
    face?.getAnimations().forEach(animation => animation.cancel());
    face?.animate([
      { backgroundColor: color, boxShadow: `inset 0 0 0 2px ${color}` },
      { backgroundColor: "transparent", boxShadow: "inset 0 0 0 0 transparent" },
    ], { duration: 420, easing: "ease-out" });

    const rect = square.getBoundingClientRect();
    const containerRect = this.gameContainer.getBoundingClientRect();
    const burst = document.createElement("div");
    burst.className = "wordspider-bonus-burst";
    burst.setAttribute("aria-hidden", "true");
    burst.style.left = `${rect.left + rect.width / 2 - containerRect.left}px`;
    burst.style.top = `${rect.top + rect.height / 2 - containerRect.top}px`;
    burst.style.setProperty("--bonus-color", color);
    burst.style.setProperty("--bonus-size", `${rect.width}px`);

    const ring = document.createElement("span");
    ring.className = "wordspider-bonus-burst__ring";
    burst.appendChild(ring);
    for (let i = 0; i < 10; i++) {
      const shard = document.createElement("span");
      const angle = (i / 10) * Math.PI * 2 - Math.PI / 2;
      const distance = rect.width * (0.85 + (i % 3) * 0.22);
      shard.className = "wordspider-bonus-burst__shard";
      shard.style.setProperty("--flight-x", `${Math.cos(angle) * distance}px`);
      shard.style.setProperty("--flight-y", `${Math.sin(angle) * distance}px`);
      shard.style.setProperty("--spin", `${(i % 2 ? 1 : -1) * (80 + i * 17)}deg`);
      shard.style.setProperty("--duration", `${480 + (i % 3) * 65}ms`);
      burst.appendChild(shard);
    }
    this.gameContainer.appendChild(burst);
    window.setTimeout(() => burst.remove(), 700);
  }

  private clearBonusBursts(): void {
    this.gameContainer.querySelectorAll(".wordspider-bonus-burst").forEach(burst => burst.remove());
  }

  private clearSquareOccupant(squareElement: HTMLElement): boolean {
    const existingTileId = squareElement.getAttribute("data-letter");
    let removed = false;

    if (existingTileId && existingTileId.trim() !== "") {
      const tile = this.tileManager.getTileById(existingTileId);
      if (tile) {
        tile.resetTransform();
        tile.setZIndex(null);
        removed = true;
      } else {
        const element = document.getElementById(existingTileId);
        if (element) {
          element.style.transform = "";
          element.style.zIndex = "";
        }
      }
    }
    squareElement.setAttribute("data-letter", "");
    return removed;
  }

  private detachTileFromCurrentSquare(tileId: string): void {
    if (!tileId) {
      return;
    }
    const selectorId = this.escapeCssSelector(tileId);
    const currentSquare = this.boardContainer.querySelector<HTMLElement>(
      `[data-letter="${selectorId}"]`,
    );
    if (currentSquare) {
      currentSquare.setAttribute("data-letter", "");
    }
  }

  private toggleTypingDirection(): void {
    this.typingDirection =
      this.typingDirection === "horizontal" ? "vertical" : "horizontal";
    this.updateTypingDirectionIndicator();
  }

  private updateTypingDirectionIndicator(): void {
    this.gameContainer.setAttribute(
      "data-typing-direction",
      this.typingDirection,
    );
  }

  private advanceSelectionAfterPlacement(): void {
    if (!this.selectedSquareId) {
      return;
    }
    if (this.typingDirection === "horizontal") {
      this.moveSelection(0, 1);
    } else {
      this.moveSelection(1, 0);
    }
  }

  private moveSelection(deltaRow: number, deltaCol: number): void {
    if (!this.selectedSquareId) {
      this.selectSquare(0, 0);
      return;
    }

    const coords = this.parseSquareId(this.selectedSquareId);
    if (!coords) {
      this.clearSelectedSquare();
      return;
    }

    const nextRow = this.clamp(coords.row + deltaRow, 0, this.gridSize - 1);
    const nextCol = this.clamp(coords.col + deltaCol, 0, this.gridSize - 1);
    this.selectSquare(nextRow, nextCol);
  }

  private selectSquare(row: number, col: number): void {
    const clampedRow = this.clamp(row, 0, this.gridSize - 1);
    const clampedCol = this.clamp(col, 0, this.gridSize - 1);
    const squareId = this.getSquareId(clampedRow, clampedCol);
    this.setSelectedSquare(squareId);
  }

  private getSquareId(row: number, col: number): string {
    return `board-${row}-${col}`;
  }

  private setSelectedSquare(id: string | null): void {
    if (this.selectedSquareId === id) {
      if (id) {
        const existing = document.getElementById(id);
        existing?.classList.add("wordspider-board-square--selected");
      }
      return;
    }

    if (this.selectedSquareId && this.selectedSquareId !== id) {
      const previous = document.getElementById(this.selectedSquareId);
      previous?.classList.remove("wordspider-board-square--selected");
    }

    this.selectedSquareId = id;

    if (id) {
      const current = document.getElementById(id);
      if (current) {
        current.classList.add("wordspider-board-square--selected");
        current.focus({ preventScroll: true });
        return;
      }

      this.selectedSquareId = null;
    }
  }

  private clearSelectedSquare(): void {
    if (!this.selectedSquareId) {
      return;
    }
    const current = document.getElementById(this.selectedSquareId);
    current?.classList.remove("wordspider-board-square--selected");
    this.selectedSquareId = null;
  }

  private parseSquareId(
    id: string | null,
  ): { row: number; col: number } | null {
    if (!id) {
      return null;
    }

    const match = /^board-(\d+)-(\d+)$/.exec(id);
    if (!match) {
      return null;
    }

    const row = Number(match[1]);
    const col = Number(match[2]);
    if (Number.isNaN(row) || Number.isNaN(col)) {
      return null;
    }

    return { row, col };
  }

  private escapeCssSelector(value: string): string {
    if (typeof CSS !== "undefined" && typeof CSS.escape === "function") {
      return CSS.escape(value);
    }
    return value.replace(/(["\\])/g, "\\$1");
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }

  private newGame(): void {
    this.clearBonusBursts();
    this.tileManager.clear();
    // Clear existing tiles and start fresh
    this.clearBoard();
    this.clearLetters();

    this.seedData = null;
    this.personalBestScore = null;
    this.renderPersonalBest();

    // Generate new bonus board layout
    this.bonusBoard = this.createBonusBoard();

    // Recreate the game with new letters
    this.initializeGame();
  }

  private calculateAndShowScore(): void {
    this.cancelPendingScoreSave();
    // Convert our tile system to the scoring system format
    const board = this.convertBoardForScoring();

    const connected = checkConnectivity(board);
    const result = calculateScore(board);
    const wordScoreSummary = this.formatWordScoreSummary(result);
    const message = [
      `Score: ${result.score}`,
      "",
      `Valid words (${result.validWords.length}):`,
      wordScoreSummary || "None",
    ];

    if (!connected) {
      message.push("", "Error: All placed letters must be connected.");
    }
    if (result.invalidWords.length > 0) {
      message.push(
        "",
        `Invalid words (${result.invalidWords.length}):`,
        result.invalidWords.join(", "),
        "Only valid words contribute to your score.",
      );
    }
    if (!connected || !result.isValid) {
      message.push("", "This attempt will not be added to the leaderboard.");
    } else if (result.validWords.length === 0) {
      message.push("", "No words found. Try connecting letters to form words!");
    }
    alert(message.join("\n"));

    void this.submitHighScore(board, result);
  }

  private async submitHighScore(
    board: (WordSpiderCell | null)[][],
    result: ScoreResult,
  ): Promise<void> {
    const seed = this.seedData?.seed;
    if (!seed) {
      return;
    }
    if (
      !result ||
      typeof result.score !== "number" ||
      result.score <= 0 ||
      !result.isValid ||
      !checkConnectivity(board) ||
      !Array.isArray(result.validWords) ||
      result.validWords.length === 0
    ) {
      return;
    }

    const serializedState: WordSpiderSerializedState = buildSerializedState({
      board,
      rack: this.letterRack,
      bonusBoard: this.bonusBoard,
    });

    try {
      const response = await fetch("/high-scores/wordspider", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          seed,
          placements: serializedState.placements,
        }),
      });

      if (!response.ok) throw new Error(`Failed to save high score (${response.status})`);
      const data = await response.json();
      if (!Number.isFinite(data.personalBest)) throw new Error("Invalid saved best response");
      if (this.seedData?.seed !== seed) return;
      this.personalBestScore = Math.max(this.personalBestScore ?? 0, data.personalBest);
      this.renderPersonalBest();
    } catch (error) {
      if (this.seedData?.seed === seed && this.bestNote && result.score > (this.personalBestScore ?? 0)) {
        this.bestNote.textContent = "Save failed. Try Score Details.";
      }
      console.warn("WordSpider: Error submitting high score", error);
    }
  }

  private convertBoardForScoring(): (WordSpiderCell | null)[][] {
    const board: (WordSpiderCell | null)[][] = [];

    for (let row = 0; row < this.gridSize; row++) {
      board[row] = [];
      for (let col = 0; col < this.gridSize; col++) {
        const tileId = `board-${row}-${col}`;
        const tileElement = document.getElementById(tileId);

        if (tileElement) {
          // Check if this board square has a letter tile dropped on it
          const letterTileId = tileElement.getAttribute("data-letter");
          let letterContent = null;

          if (letterTileId && letterTileId.trim() !== "") {
            // Find the actual letter tile by its ID
            const letterTileElement = document.getElementById(letterTileId);
            if (letterTileElement) {
              // Get the letter from the actual letter tile's data-letter attribute
              letterContent = letterTileElement.getAttribute("data-letter");
            }
          }

          // Get bonus information from the tile element
          const bonusType = this.parseBonusValue(
            tileElement.getAttribute("data-bonus"),
          );

          const normalizedLetter =
            letterContent && letterContent.trim() !== ""
              ? letterContent.trim().toUpperCase()
              : null;

          board[row][col] = {
            letter: normalizedLetter,
            bonus: bonusType,
          };
        } else {
          board[row][col] = {
            letter: null,
            bonus: "none",
          };
        }
      }
    }

    // Log the final board structure

    return board;
  }
  private clearBoard(): void {
    // Remove all tiles from board
    this.clearSelectedSquare();
    this.boardContainer.innerHTML = "";
  }

  private clearLetters(): void {
    // Remove all tiles from letters container
    this.lettersContainer.innerHTML = "";
    this.letterRack = [];
  }

  private resetBoard(): void {
    this.clearBonusBursts();
    // Find all letter tiles that are currently on board squares
    const boardSquares = document.querySelectorAll('[id^="board-"]');

    for (let i = 0; i < boardSquares.length; i++) {
      const square = boardSquares[i] as HTMLElement;
      const tileId = square.getAttribute("data-letter");

      if (tileId && tileId.trim() !== "") {
        // Find the actual tile element
        const tileElement = document.getElementById(tileId);
        if (tileElement) {
          // Reset the tile's transform to move it back to original position
          tileElement.style.transform = "";
          tileElement.style.zIndex = "";
        }

        // Clear the board square's data-letter attribute
        square.setAttribute("data-letter", "");
      }
    }
  }

  private getLettersForGame(): string[] {
    const seeded = this.getSeededLetters();
    if (seeded) {
      return seeded;
    }
    return generateLetters();
  }

  private getSeededLetters(): string[] | null {
    const lettersString = this.seedData?.letters;
    if (lettersString?.length !== this.letterCount) {
      return null;
    }
    return lettersString.split("");
  }

  private formatWordScoreSummary(result: ScoreResult): string {
    if (!result.wordScores.length) {
      return "";
    }
    return result.wordScores
      .map((detail) => `${detail.word} (${detail.score})`)
      .join(", ");
  }

  private parseBonusValue(value: string | null): WordSpiderBonusType {
    const normalized = (value || "none").trim();
    switch (normalized) {
      case "2xWord":
      case "3xWord":
      case "2xLetter":
      case "3xLetter":
      case "+10":
      case "+50":
      case "none":
        return normalized;
      default:
        return "none";
    }
  }

  private async toggleLeaderboard(): Promise<void> {
    if (this.leaderboardPanel) {
      this.leaderboardPanel.remove();
      this.leaderboardPanel = null;
      return;
    }

    const seed = this.seedData?.seed;
    if (!seed) {
      alert("Leaderboard is only available for seeded boards.");
      return;
    }

    const panel = this.buildLeaderboardPanel();
    this.leaderboardPanel = panel;
    document.body.appendChild(panel);

    try {
      await this.populateLeaderboard(panel, seed);
    } catch (error) {
      console.warn("WordSpider: Failed to load leaderboard", error);
      this.renderLeaderboardError(
        panel,
        "Unable to load the leaderboard right now. Please try again later.",
      );
    }
  }

  private buildLeaderboardPanel(): HTMLElement {
    const wrapper = document.createElement("div");
    wrapper.className = "wordspider-leaderboard";
    wrapper.innerHTML = `
      <div class="wordspider-leaderboard__panel">
        <div class="wordspider-leaderboard__header">
          <h2>Leaderboard</h2>
          <button type="button" class="wordspider-leaderboard__close" aria-label="Close">×</button>
        </div>
        <div class="wordspider-leaderboard__body">
          <div class="wordspider-leaderboard__loading">Loading...</div>
        </div>
      </div>
    `;

    wrapper
      .querySelector(".wordspider-leaderboard__close")
      ?.addEventListener("click", () => {
        wrapper.remove();
        this.leaderboardPanel = null;
      });

    wrapper.addEventListener("click", (event) => {
      if (event.target === wrapper) {
        wrapper.remove();
        this.leaderboardPanel = null;
      }
    });

    return wrapper;
  }

  private async populateLeaderboard(
    panel: HTMLElement,
    seed: string,
  ): Promise<void> {
    if (this.leaderboardLoading) {
      return;
    }

    this.leaderboardLoading = true;

    const body = panel.querySelector(".wordspider-leaderboard__body");

    try {
      const entries = await fetchLeaderboard(seed);
      if (!entries.length) {
        body.innerHTML =
          "<p>No entries yet. Be the first to post a score for this board!</p>";
        return;
      }
      body.innerHTML = this.renderLeaderboardTable(entries);
    } finally {
      this.leaderboardLoading = false;
    }
  }

  private renderLeaderboardTable(
    entries: WordSpiderLeaderboardEntry[],
  ): string {
    const rows = entries
      .map(
        (entry, index) => `
        <tr>
          <td>${index + 1}</td>
          <td>${escapeHtml(entry.displayName || "Guest")}</td>
          <td>${entry.score}</td>
          <td>${entry.updatedAt ? new Date(entry.updatedAt).toLocaleDateString() : ""}</td>
        </tr>
      `,
      )
      .join("");

    return `
      <table class="wordspider-leaderboard__table">
        <thead>
          <tr>
            <th>#</th>
            <th>Player</th>
            <th>Score</th>
            <th>Updated</th>
          </tr>
        </thead>
        <tbody>
          ${rows}
        </tbody>
      </table>
    `;
  }

  private renderLeaderboardError(panel: HTMLElement, message: string): void {
    const body = panel.querySelector(".wordspider-leaderboard__body");
    if (body) {
      body.innerHTML = `<p class="wordspider-leaderboard__error">${escapeHtml(message)}</p>`;
    }
  }

  private getBonusDisplayText(bonus: string): string {
    switch (bonus) {
      case "2xWord":
        return "2x\nWord";
      case "3xWord":
        return "3x\nWord";
      case "2xLetter":
        return "2x\nLetter";
      case "3xLetter":
        return "3x\nLetter";
      case "+10":
        return "+10";
      case "+50":
        return "+50";
      default:
        return "";
    }
  }
}

// Initialize the game when DOM is ready
document.addEventListener("DOMContentLoaded", () => {
  try {
    new WordSpiderGame();
  } catch (error) {
    console.error("Failed to initialize WordSpider game:", error);
  }
});

export default WordSpiderGame;
